/**
 * Module de personnalisation Eternel Flowers — thème Horizon.
 *
 * Rend les groupes d'options d'un produit, applique les règles d'incompatibilité
 * et met à jour le prix en direct.
 *
 * Intégration au panier :
 * - les choix sont recopiés dans des champs cachés `properties[…]` rattachés au
 *   formulaire d'achat par l'attribut `form`. Horizon sérialise le formulaire
 *   avec `new FormData(form)` : les personnalisations partent donc nativement,
 *   sans intercepter son composant ;
 * - le montant des suppléments est facturé par une seconde ligne de panier,
 *   ajoutée juste avant l'ajout du bouquet (produit technique à 0,50 € l'unité).
 */
(() => {
  const SELECTEUR_RACINE = '[data-ef-personnalisation]';
  const ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');
  const CHIFFRES = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9'];

  const lireConfig = () => {
    const balise = document.getElementById('ef-personnalisation-config');
    if (!balise) return null;
    try {
      return JSON.parse(balise.textContent);
    } catch (erreur) {
      console.error('[Eternel Flowers] Configuration de personnalisation illisible.', erreur);
      return null;
    }
  };

  const formaterEuros = (montant, devise) =>
    new Intl.NumberFormat('fr-FR', { style: 'currency', currency: devise || 'EUR' }).format(montant);

  const echapper = (texte) =>
    String(texte).replace(/[&<>"']/g, (c) =>
      ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

  /**
   * Lit un montant dans un texte de prix du thème.
   * Gère « €20,00 », « 20,00 € » et « 1 190,00 € » : virgule décimale,
   * espaces insécables pour les milliers.
   */
  const montantDepuisTexte = (texte) => {
    if (!texte) return null;
    const nettoye = texte.replace(/[^\d.,]/g, '');
    if (!nettoye) return null;
    // La virgule sépare les décimales ; le point sépare les milliers.
    const normalise = nettoye.includes(',')
      ? nettoye.replace(/\./g, '').replace(',', '.')
      : nettoye;
    const valeur = Number.parseFloat(normalise);
    return Number.isFinite(valeur) ? valeur : null;
  };

  /**
   * Prix affiché par le thème pour la variante choisie.
   * On écarte la barre collante et les cartes de produits recommandés, qui
   * portent la même classe.
   */
  const trouverPrixDuTheme = (racine) => {
    const bloc = racine.closest('.product-details') || document;
    return [...bloc.querySelectorAll('.price')].find(
      (p) => !p.closest('sticky-add-to-cart') && !p.closest('[class*="product-card"]'),
    ) ?? null;
  };

  /** Construit l'index des paires incompatibles : id -> Set des ids bloqués. */
  const indexerIncompatibilites = (paires) => {
    const index = new Map();
    for (const [a, b] of paires) {
      if (!index.has(a)) index.set(a, new Set());
      if (!index.has(b)) index.set(b, new Set());
      index.get(a).add(b);
      index.get(b).add(a);
    }
    return index;
  };

  /** Contrôle de saisie adapté au type de champ, pour éviter les valeurs invalides. */
  const gabaritSaisie = (champ, idOption) => {
    const commun = `class="ef-champ__saisie"`
      + ` data-ef-propriete="${echapper(champ.label)}"`
      + ` data-ef-champ-id="${echapper(champ.id)}"`
      + ` data-ef-champ-de-option="${echapper(idOption)}"`;

    const choix = (valeurs) => `
      <select ${commun}>
        <option value="">— à choisir —</option>
        ${valeurs.map((v) => `<option value="${echapper(v)}">${echapper(v)}</option>`).join('')}
      </select>`;

    switch (champ.type) {
      case 'liste':
        return choix(champ.options ?? []);
      case 'lettre':
        return choix(ALPHABET);
      case 'chiffre':
        return choix(CHIFFRES);
      case 'nombre':
        return `<input type="number" inputmode="numeric" min="10" max="99" step="1" ${commun}>`;
      case 'message':
        return `<textarea rows="3" maxlength="200" ${commun}></textarea>`;
      default:
        return `<input type="text" maxlength="120" ${commun}>`;
    }
  };

  const gabaritOption = (groupe, option) => {
    const nom = groupe.type === 'radio' ? `ef-${groupe.id}` : `ef-${option.id}`;
    const valeur = groupe.type === 'radio' ? option.id : '1';
    const prixTexte = option.prix > 0
      ? `<span class="ef-option__prix">+ ${formaterEuros(option.prix)}</span>`
      : '<span class="ef-option__prix ef-option__prix--inclus">inclus</span>';

    // Une option peut porter plusieurs réglages : le ruban demande sa couleur,
    // son texte et la couleur de l'écriture.
    const champs = (option.champs ?? []).length
      ? `<div class="ef-champs" data-ef-champ-de="${echapper(option.id)}" hidden>
           ${option.champs.map((c) => `
             <label class="ef-champ">
               <span class="ef-champ__label">${echapper(c.label)}</span>
               ${gabaritSaisie(c, option.id)}
             </label>`).join('')}
           ${option.avertirSiIdentiques
             ? `<p class="ef-avertissement" data-ef-avertissement="${echapper(option.id)}" role="status" hidden></p>`
             : ''}
         </div>`
      : '';

    return `
      <div class="ef-option" data-ef-option="${echapper(option.id)}">
        <label class="ef-option__ligne">
          <input type="${groupe.type}" name="${echapper(nom)}" value="${echapper(valeur)}"
                 class="ef-option__saisie"
                 data-ef-id="${echapper(option.id)}"
                 data-ef-groupe="${echapper(groupe.id)}"
                 data-ef-prix="${option.prix}"
                 data-ef-label="${echapper(option.label)}"
                 ${groupe.type === 'radio' && option.defaut ? 'checked' : ''}>
          <span class="ef-option__nom">${echapper(option.label)}</span>
          ${prixTexte}
        </label>
        ${champs}
      </div>`;
  };

  const gabaritGroupe = (groupe) => `
    <fieldset class="ef-groupe" data-ef-groupe="${echapper(groupe.id)}">
      <legend class="ef-groupe__titre">${echapper(groupe.label)}</legend>
      ${groupe.aide ? `<p class="ef-groupe__aide">${echapper(groupe.aide)}</p>` : ''}
      <div class="ef-groupe__options">
        ${groupe.options.map((o) => gabaritOption(groupe, o)).join('')}
      </div>
    </fieldset>`;

  const gabaritPrecommande = (precommande) => `
    <fieldset class="ef-groupe ef-groupe--precommande">
      <legend class="ef-groupe__titre">${echapper(precommande.label)}</legend>
      <p class="ef-groupe__aide">${echapper(precommande.aide)}</p>
      <input type="date" class="ef-champ__saisie" data-ef-precommande
             data-ef-propriete="Date souhaitée">
    </fieldset>`;

  class Personnalisation {
    constructor(racine, config) {
      this.racine = racine;
      this.config = config;
      this.incompatibilites = indexerIncompatibilites(config.incompatibles || []);
      this.pas = config.pasSupplement || 0.5;
      this.idSupplement = Number(racine.dataset.efSupplementVariante) || null;
      this.prixBase = Number(racine.dataset.efPrixBase) || 0;
      this.idFormulaire = racine.dataset.efFormulaire || '';
      this.formulaire = this.idFormulaire ? document.getElementById(this.idFormulaire) : null;
      this.supplementEnCours = false;

      // Peluches vendues seules : le marquage n'a de sens que sur la variante
      // « Avec personnalisation ». Le bloc du thème en transmet les identifiants.
      this.variantesPersonnalisees = (racine.dataset.efVariantesPersonnalisees || '')
        .split(',')
        .map((v) => v.trim())
        .filter(Boolean);

      this.rendre();
      this.brancher();
      this.suivrePrixDuTheme();
      this.suivreVariante();
      this.rafraichir();
    }

    rendre() {
      this.racine.innerHTML = `
        <div class="ef-perso">
          <p class="ef-perso__incluse">${echapper(this.config.mentionIncluse)}</p>
          ${this.config.groupes.map(gabaritGroupe).join('')}
          ${gabaritPrecommande(this.config.precommande)}
          <div class="ef-total">
            <p class="ef-total__ligne">
              <span>Bouquet</span>
              <span data-ef-prix-base-affiche>${formaterEuros(this.prixBase)}</span>
            </p>
            <p class="ef-total__ligne">
              <span>Personnalisations</span>
              <span data-ef-total-montant>${formaterEuros(0)}</span>
            </p>
            <p class="ef-total__ligne ef-total__ligne--somme">
              <span class="ef-total__label">Total</span>
              <span class="ef-total__montant" data-ef-total-general>${formaterEuros(this.prixBase)}</span>
            </p>
          </div>
          <p class="ef-perso__recap" data-ef-recap hidden></p>
          <div data-ef-proprietes hidden></div>
        </div>`;
      this.saisies = [...this.racine.querySelectorAll('.ef-option__saisie')];
      this.zoneProprietes = this.racine.querySelector('[data-ef-proprietes]');
    }

    brancher() {
      this.racine.addEventListener('change', () => this.rafraichir());
      this.racine.addEventListener('input', () => {
        this.majRecapitulatif();
        this.majAvertissements();
        this.synchroniserProprietes();
      });

      // Le supplément est ajouté avant que Horizon n'ajoute le bouquet lui-même.
      const bouton = this.boutonAjout();
      if (bouton) bouton.addEventListener('click', (e) => this.avantAjout(e), true);
    }

    boutonAjout() {
      if (!this.formulaire) return null;
      return this.formulaire.querySelector('add-to-cart-component button, button[type="submit"]');
    }

    /** Identifiants actuellement sélectionnés. */
    selection() {
      return this.saisies.filter((s) => s.checked).map((s) => s.dataset.efId);
    }

    /** Recalcule blocages, champs visibles, total et propriétés transmises. */
    rafraichir() {
      const choisis = new Set(this.selection());
      const bloques = new Set();
      for (const id of choisis) {
        for (const autre of this.incompatibilites.get(id) || []) bloques.add(autre);
      }

      for (const saisie of this.saisies) {
        const id = saisie.dataset.efId;
        const estBloque = bloques.has(id) && !choisis.has(id);
        saisie.disabled = estBloque;
        saisie.closest('.ef-option').classList.toggle('ef-option--bloquee', estBloque);
        const champs = this.racine.querySelector(`[data-ef-champ-de="${CSS.escape(id)}"]`);
        if (champs) {
          champs.hidden = !saisie.checked;
          if (!saisie.checked) {
            for (const entree of champs.querySelectorAll('.ef-champ__saisie')) entree.value = '';
          }
        }
      }

      this.majAvertissements();

      this.total = this.saisies
        .filter((s) => s.checked && !s.disabled)
        .reduce((somme, s) => somme + Number(s.dataset.efPrix), 0);

      this.majTotaux();
      this.majRecapitulatif();
      this.synchroniserProprietes();
    }

    /**
     * Prévient quand deux réglages d'une même option portent la même valeur —
     * un ruban et une écriture de la même couleur, par exemple. On avertit sans
     * bloquer : c'est un conseil, pas une règle de fabrication.
     */
    majAvertissements() {
      for (const groupe of this.config.groupes) {
        for (const option of groupe.options) {
          const regle = option.avertirSiIdentiques;
          if (!regle) continue;

          const zone = this.racine.querySelector(
            `[data-ef-avertissement="${CSS.escape(option.id)}"]`,
          );
          if (!zone) continue;

          const valeurs = regle.champs.map((idChamp) => {
            const entree = this.racine.querySelector(
              `[data-ef-champ-de-option="${CSS.escape(option.id)}"][data-ef-champ-id="${CSS.escape(idChamp)}"]`,
            );
            return entree?.value.trim() ?? '';
          });

          const toutesRenseignees = valeurs.every(Boolean);
          const identiques = toutesRenseignees && new Set(valeurs).size === 1;
          zone.hidden = !identiques;
          zone.textContent = identiques ? regle.message : '';
        }
      }
    }

    majRecapitulatif() {
      const recap = this.racine.querySelector('[data-ef-recap]');
      const choix = this.saisies
        .filter((s) => s.checked && !s.disabled && Number(s.dataset.efPrix) > 0)
        .map((s) => s.dataset.efLabel);
      recap.hidden = choix.length === 0;
      recap.textContent = choix.length ? `Votre sélection : ${choix.join(', ')}.` : '';
    }

    /**
     * Suit le prix affiché par le thème : il change quand la cliente choisit
     * un autre emballage. Sans cette écoute, le total resterait sur le prix
     * de la première variante.
     */
    suivrePrixDuTheme() {
      // Horizon ne réécrit pas le texte du prix : il remplace le nœud entier.
      // On surveille donc le bloc de la fiche et on retrouve le prix à chaque
      // fois, plutôt que de garder une référence qui serait vite détachée.
      const bloc = this.racine.closest('.product-details');
      if (!bloc) return;

      let planifie = false;
      const relire = () => {
        const noeud = trouverPrixDuTheme(this.racine);
        const montant = montantDepuisTexte(noeud?.textContent);
        if (montant === null || montant === this.prixBase) return;
        this.prixBase = montant;
        this.majTotaux();
      };

      relire();

      this.observateurPrix = new MutationObserver(() => {
        // Le remplacement déclenche une rafale de mutations : on n'en garde
        // qu'une lecture par image.
        if (planifie) return;
        planifie = true;
        requestAnimationFrame(() => {
          planifie = false;
          relire();
        });
      });

      this.observateurPrix.observe(bloc, {
        characterData: true,
        childList: true,
        subtree: true,
      });
    }

    /**
     * Masque les réglages réservés à certaines variantes — le marquage d'une
     * peluche vendue sans personnalisation, par exemple. Les champs cachés sont
     * vidés pour qu'aucune consigne inutile ne parte à l'atelier.
     */
    suivreVariante() {
      if (!this.config.reserveAuxVariantes || !this.formulaire) return;

      const champId = this.formulaire.querySelector('[name="id"]');
      if (!champId) return;

      const appliquer = () => {
        const active = this.variantesPersonnalisees.includes(String(champId.value));
        this.racine.classList.toggle('ef-perso--masquee', !active);
        if (!active) {
          for (const saisie of this.saisies) {
            saisie.checked = Boolean(
              this.config.groupes
                .flatMap((g) => g.options)
                .find((o) => o.id === saisie.dataset.efId)?.defaut,
            );
          }
          this.rafraichir();
        }
      };

      appliquer();

      // Horizon réécrit la valeur du champ caché à chaque changement de variante.
      this.observateurVariante = new MutationObserver(appliquer);
      this.observateurVariante.observe(champId, { attributes: true, attributeFilter: ['value'] });
      this.formulaire.addEventListener('change', appliquer);
    }

    /** Met à jour les trois lignes du récapitulatif de prix. */
    majTotaux() {
      const ecrire = (selecteur, montant) => {
        const cible = this.racine.querySelector(selecteur);
        if (cible) cible.textContent = formaterEuros(montant);
      };
      ecrire('[data-ef-prix-base-affiche]', this.prixBase);
      ecrire('[data-ef-total-montant]', this.total);
      ecrire('[data-ef-total-general]', this.prixBase + this.total);
    }

    /** Toutes les personnalisations à transmettre à l'atelier. */
    proprietes() {
      const props = {};
      for (const saisie of this.saisies) {
        if (!saisie.checked || saisie.disabled) continue;
        if (Number(saisie.dataset.efPrix) === 0) continue;
        props[saisie.dataset.efLabel] = 'Oui';
      }
      for (const champ of this.racine.querySelectorAll('[data-ef-propriete]')) {
        const valeur = champ.value.trim();
        if (valeur) props[champ.dataset.efPropriete] = valeur;
      }
      if (this.total > 0) props['Total personnalisations'] = formaterEuros(this.total);
      return props;
    }

    /**
     * Recopie les personnalisations dans des champs cachés rattachés au
     * formulaire d'achat. Horizon les inclut alors dans son propre envoi.
     */
    synchroniserProprietes() {
      if (!this.zoneProprietes) return;
      const props = this.proprietes();
      this.zoneProprietes.innerHTML = Object.entries(props)
        .map(([cle, valeur]) => `
          <input type="hidden" name="properties[${echapper(cle)}]"
                 value="${echapper(valeur)}"
                 ${this.idFormulaire ? `form="${echapper(this.idFormulaire)}"` : ''}>`)
        .join('');
    }

    /** Nombre d'unités du produit technique correspondant au total. */
    unitesSupplement(quantite) {
      return Math.round((this.total / this.pas) * quantite);
    }

    /**
     * Ajoute la ligne de supplément avant de laisser Horizon ajouter le bouquet.
     * Sans cela, le montant des personnalisations ne serait pas facturé.
     */
    async avantAjout(evenement) {
      if (this.supplementEnCours || this.total <= 0 || !this.idSupplement) return;
      if (!this.formulaire.checkValidity()) return;

      evenement.preventDefault();
      evenement.stopImmediatePropagation();

      const quantite = Number(new FormData(this.formulaire).get('quantity')) || 1;
      const unites = this.unitesSupplement(quantite);
      if (unites <= 0) return;

      const bouton = evenement.currentTarget;
      bouton.setAttribute('aria-busy', 'true');

      try {
        const racineUrl = window.Shopify?.routes?.root || '/';
        const reponse = await fetch(`${racineUrl}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            items: [{
              id: this.idSupplement,
              quantity: unites,
              properties: { 'Se rapporte à': this.config.titre },
            }],
          }),
        });
        if (!reponse.ok) {
          const erreur = await reponse.json().catch(() => ({}));
          throw new Error(erreur.description || `Supplément refusé (${reponse.status}).`);
        }

        // Le supplément est en place : on relance le bouton pour laisser
        // Horizon ajouter le bouquet et ouvrir son panier.
        this.supplementEnCours = true;
        bouton.click();
      } catch (erreur) {
        console.error('[Eternel Flowers] Supplément non ajouté.', erreur);
        this.afficherErreur(erreur.message);
      } finally {
        bouton.removeAttribute('aria-busy');
        this.supplementEnCours = false;
      }
    }

    afficherErreur(message) {
      let zone = this.racine.querySelector('.ef-erreur');
      if (!zone) {
        zone = document.createElement('p');
        zone.className = 'ef-erreur';
        zone.setAttribute('role', 'alert');
        this.racine.append(zone);
      }
      zone.textContent = `${message} Réessayez ou écrivez-nous à contacteternelflowers@gmail.com.`;
    }
  }

  const demarrer = () => {
    const configuration = lireConfig();
    if (!configuration) return;
    for (const racine of document.querySelectorAll(SELECTEUR_RACINE)) {
      if (racine.dataset.efMonte === '1') continue;
      const config = configuration[racine.dataset.efPersonnalisation];
      if (!config) continue;
      racine.dataset.efMonte = '1';
      new Personnalisation(racine, config);
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', demarrer);
  } else {
    demarrer();
  }

  // L'éditeur de thème recharge les sections sans recharger la page.
  document.addEventListener('shopify:section:load', demarrer);
})();
